import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { AuthModal } from '../components/AuthModal'
import { Button } from '../components/ui/button'
import { useAuth } from '../context/AuthContext'
import { useTenant } from '../context/TenantContext'
import {
  ArrowRight,
  Wallet,
  ShieldCheck,
  UserCheck,
  CreditCard,
  BarChart3,
  TrendingUp,
  PieChart,
  Globe,
  Scale,
  ChevronDown,
  Sparkles,
  Target,
  Clock,
  BadgeCheck
} from 'lucide-react'

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }

export default function InvestorsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { tenant } = useTenant()
  const [authOpen, setAuthOpen] = useState(false)

  const steps = [
    {
      num: '01',
      icon: UserCheck,
      title: t('investors.steps.register.title'),
      description: t('investors.steps.register.description'),
    },
    {
      num: '02',
      icon: CreditCard,
      title: t('investors.steps.fund.title'),
      description: t('investors.steps.fund.description'),
    },
    {
      num: '03',
      icon: BarChart3,
      title: t('investors.steps.trade.title'),
      description: t('investors.steps.trade.description'),
    },
  ]

  const advantages = [
    { icon: PieChart, title: t('investors.advantages.fractional.title'), description: t('investors.advantages.fractional.description') },
    { icon: Globe, title: t('investors.advantages.global.title'), description: t('investors.advantages.global.description') },
    { icon: ShieldCheck, title: t('investors.advantages.regulated.title'), description: t('investors.advantages.regulated.description') },
    { icon: Clock, title: t('investors.advantages.always.title'), description: t('investors.advantages.always.description') },
    { icon: TrendingUp, title: t('investors.advantages.yields.title'), description: t('investors.advantages.yields.description') },
    { icon: Target, title: t('investors.advantages.diversify.title'), description: t('investors.advantages.diversify.description') },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center bg-brand-secondary overflow-hidden" data-testid="investors-hero">
        <div className="absolute inset-0 hero-bg-kenburns">
          <img
            src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=1920&q=80&auto=format"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-secondary/90 via-brand-secondary/75 to-brand-secondary/95" />
        <div className="absolute inset-0 hero-radial-glow" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.span variants={fadeUp}
                className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-8 backdrop-blur-sm">
                {t('investors.hero.badge')}
              </motion.span>
              <motion.h1 variants={fadeUp}
                className="text-4xl md:text-5xl lg:text-6xl font-heading text-white leading-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
                {t('investors.hero.title1')}{' '}
                <span className="text-brand-primary hero-text-glow">{t('investors.hero.title2')}</span>
              </motion.h1>
              <motion.div variants={fadeUp} className="w-16 h-px bg-brand-primary my-8 shadow-[0_0_12px_rgba(var(--brand-primary-rgb)/0.6)]" />
              <motion.p variants={fadeUp} className="text-lg text-white/70 font-body drop-shadow-md max-w-lg">
                {t('investors.hero.subtitle')}
              </motion.p>
              <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row gap-4">
                <Button onClick={() => isAuthenticated ? navigate('/marketplace') : setAuthOpen(true)}
                  className="h-16 px-10 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide text-base rounded-none shadow-[0_0_30px_rgba(var(--brand-primary-rgb)/0.3)]"
                  data-testid="investors-hero-cta">
                  <Wallet className="w-5 h-5 mr-2" />{t('investors.hero.cta')}<ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </motion.div>

            {/* Stats column */}
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}
              className="hidden lg:grid grid-cols-2 gap-4">
              {[
                { value: '$100', label: t('investors.hero.stat1') },
                { value: '8.5%', label: t('investors.hero.stat2') },
                { value: '24/7', label: t('investors.hero.stat3') },
                { value: '100%', label: t('investors.hero.stat4') },
              ].map((s, i) => (
                <div key={i} className="p-6 border border-white/10 bg-white/5 backdrop-blur-sm text-center">
                  <p className="text-2xl font-heading text-brand-primary">{s.value}</p>
                  <p className="text-xs text-white/60 mt-1 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fractional Ownership Explainer */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="investors-fractional">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.span variants={fadeUp}
                className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-6">
                {t('investors.fractional.badge')}
              </motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-heading text-brand-secondary mb-6">
                {t('investors.fractional.title')}
              </motion.h2>
              <motion.div variants={fadeUp} className="w-16 h-px bg-brand-primary mb-6" />
              <motion.p variants={fadeUp} className="text-slate-600 leading-relaxed mb-4">
                {t('investors.fractional.p1')}
              </motion.p>
              <motion.p variants={fadeUp} className="text-slate-600 leading-relaxed">
                {t('investors.fractional.p2')}
              </motion.p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="relative">
              <div className="bg-brand-secondary p-8 lg:p-10">
                <Sparkles className="w-12 h-12 text-brand-primary mb-6" />
                <h3 className="text-xl font-heading text-white mb-4">{t('investors.fractional.cardTitle')}</h3>
                <div className="space-y-4">
                  {[
                    t('investors.fractional.point1'),
                    t('investors.fractional.point2'),
                    t('investors.fractional.point3'),
                  ].map((text, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <BadgeCheck className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
                      <p className="text-white/80 text-sm leading-relaxed">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-brand-primary/20 -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3 Steps */}
      <section className="py-24 bg-white" data-testid="investors-steps">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('investors.steps.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary">{t('investors.steps.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">{t('investors.steps.subtitle')}</p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-primary/30 to-transparent -translate-y-1/2" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 }} viewport={{ once: true }}
                  className="relative bg-white border border-slate-100 p-8 hover:border-brand-primary/30 transition-all duration-300 group"
                  data-testid={`investors-step-${index + 1}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-5xl font-heading text-brand-primary/20 group-hover:text-brand-primary/40 transition-colors">{step.num}</span>
                    <div className="w-14 h-14 bg-brand-primary/10 flex items-center justify-center group-hover:bg-brand-primary/20 transition-colors">
                      <step.icon className="w-7 h-7 text-brand-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-heading text-brand-secondary mb-3">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Button onClick={() => isAuthenticated ? navigate('/marketplace') : setAuthOpen(true)}
              className="h-14 px-8 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide rounded-none"
              data-testid="investors-steps-cta">
              {t('investors.steps.cta')}<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Advantages Grid */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="investors-advantages">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('investors.advantages.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary">{t('investors.advantages.title')}</h2>
            <div className="w-16 h-px bg-brand-primary mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((adv, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }} viewport={{ once: true }}
                className="p-6 bg-white border border-slate-100 hover:border-brand-primary/30 transition-all duration-300 group"
                data-testid={`investors-advantage-${index}`}>
                <div className="w-14 h-14 bg-brand-primary/10 flex items-center justify-center mb-4 group-hover:bg-brand-primary/20 transition-colors">
                  <adv.icon className="w-7 h-7 text-brand-primary" />
                </div>
                <h3 className="text-lg font-heading text-brand-secondary mb-2">{adv.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{adv.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section className="py-24 bg-brand-secondary" data-testid="investors-why-now">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-6">
                {t('investors.whyNow.badge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-heading text-white mb-6">{t('investors.whyNow.title')}</h2>
              <div className="w-16 h-px bg-brand-primary mb-6" />
              <p className="text-white/70 leading-relaxed mb-4">{t('investors.whyNow.p1')}</p>
              <p className="text-white/70 leading-relaxed">{t('investors.whyNow.p2')}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Scale, text: t('investors.whyNow.point1') },
                { icon: Globe, text: t('investors.whyNow.point2') },
                { icon: TrendingUp, text: t('investors.whyNow.point3') },
                { icon: ShieldCheck, text: t('investors.whyNow.point4') },
              ].map((item, i) => (
                <div key={i} className="p-5 border border-white/10 bg-white/5 backdrop-blur-sm">
                  <item.icon className="w-8 h-8 text-brand-primary mb-3" />
                  <p className="text-sm text-white/80 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white" data-testid="investors-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
            {t('investors.cta.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-heading text-brand-secondary mb-4">{t('investors.cta.title')}</h2>
          <div className="w-16 h-px bg-brand-primary mx-auto my-6" />
          <p className="text-slate-500 max-w-xl mx-auto mb-10">{t('investors.cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={() => isAuthenticated ? navigate('/marketplace') : setAuthOpen(true)}
              className="h-16 px-10 bg-brand-secondary hover:bg-brand-secondary/90 text-white font-semibold tracking-wide rounded-none"
              data-testid="investors-cta-btn">
              <Wallet className="w-5 h-5 mr-2" />{t('investors.cta.button')}<ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => navigate('/marketplace')} variant="outline"
              className="h-16 px-10 border-brand-secondary text-brand-secondary hover:bg-brand-secondary hover:text-white font-semibold tracking-wide rounded-none"
              data-testid="investors-cta-explore-btn">
              {t('investors.cta.explore')}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-brand-secondary border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={tenant?.branding?.logo_url || "https://uni2ecosystem.com/images/logos/tokenizelogo.png"} alt={tenant?.name || "2kenize"} className="h-8 w-auto" />
            <p className="text-sm text-white/50">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  )
}
